import { Injectable } from '@angular/core';
import { IsOEPActionResultCode, OEP_ACTION_IDS, type OEPActionResultCode, type OEPGateKey } from 'agu_oep_core';
import { OEPGraphQLTransport, ReadGraphQLError, type OEPGqlVariables } from './oep-graphql';
import { OEP_RUN_ACTION } from './oep-operations';

/** Client mirror of apps/MJAPI/src/oep/types.ts OEPSendTally (Mode = shared/delivery DeliveryMode). */
export interface OEPTallyView { Mode: 'dryrun' | 'sandbox' | 'redirect' | 'live'; Sent: number; DryRun: number; Redirected: number; Blocked: number; Failed: number; NoEmail: number; Errors: string[] }
/** Client mirror of OEPGateSummary. */
export interface OEPGateSummaryView { Gate: OEPGateKey; FromPhase: string; ToPhase: string; StateChanges: Record<string, number>; Mail: OEPTallyView[] }
/** Client mirror of the engine's OEPSendPurpose. */
export type OEPSendPurposeView = OEPGateKey | 'Reminders' | 'Training';
/** Client mirror of the engine's PreviewSend result. */
export interface OEPSendPreviewView { Purpose: OEPSendPurposeView; Previews: { Mode: OEPTallyView['Mode']; Recipients: number; RedirectedTo: string | null }[] }

/** Why an action was refused: an action result code, or FORBIDDEN from the GraphQL Action Authorization guard (T13). */
export type OEPActionRefusalCode = Exclude<OEPActionResultCode, 'SUCCESS'> | 'FORBIDDEN';

export class OEPActionError extends Error {
    constructor(public readonly Code: OEPActionRefusalCode, message: string) {
        super(message);
        this.name = 'OEPActionError';
    }
}

interface RunActionData { RunAction: { Success: boolean; Message: string | null; ResultCode: string | null; ResultData: string | null } }
interface OutputParam { Name: string; Value: string | null; Type: string }

const DEFAULT_FAILURE = 'The action failed.';

/** JSON.parse at the wire boundary; malformed server output is an action failure, never a raw SyntaxError. */
function parseJSON<T>(text: string): T {
    try {
        return JSON.parse(text) as T;
    } catch {
        throw new OEPActionError('FAILED', DEFAULT_FAILURE);
    }
}

/**
 * ResultData's output params in index order. MJ 5.51 serializes the params array through
 * CopyScalarsAndArrays (server/dist/resolvers/ActionResolver.js:257), whose simple mode returns a
 * plain object (global/dist/util.js:125), so the wire shape is `{"0": {...}}`, not an array.
 */
function outputParamList(parsed: OutputParam[] | Record<string, OutputParam>): OutputParam[] {
    if (Array.isArray(parsed)) return parsed;
    return Object.keys(parsed).filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b)).map((k) => parsed[k]);
}

/**
 * The six OEP staff actions (apps/MJAPI/src/oep/actions.ts), run by pinned MJ Action ID.
 * Both refusal shapes become OEPActionError: a GraphQL error with extensions.code FORBIDDEN
 * (guard refused before the action ran) and `{ Success:false, ResultCode, Message }` (the
 * action ran and refused; FAILED carries the server's fixed message).
 */
@Injectable()
export class OEPActionsService {
    constructor(private readonly gql: OEPGraphQLTransport) {}

    async RunGate(gate: OEPGateKey): Promise<OEPGateSummaryView> {
        return this.output<OEPGateSummaryView>(await this.run(OEP_ACTION_IDS.RunGate, { Gate: gate }), 'Summary');
    }
    async PreviewSend(purpose: OEPSendPurposeView): Promise<OEPSendPreviewView> {
        return this.output<OEPSendPreviewView>(await this.run(OEP_ACTION_IDS.PreviewSend, { Purpose: purpose }), 'Preview');
    }
    async SendReminders(): Promise<OEPTallyView> {
        return this.output<OEPTallyView>(await this.run(OEP_ACTION_IDS.SendReminders, {}), 'Summary');
    }
    async SendTraining(): Promise<OEPTallyView> {
        return this.output<OEPTallyView>(await this.run(OEP_ACTION_IDS.SendTrainingMaterials, {}), 'Summary');
    }
    async ReopenSlate(selectionID: string): Promise<void> {
        await this.run(OEP_ACTION_IDS.ReopenSlate, { CommitteeSelectionID: selectionID });
    }

    /** Runs the action; returns its output params (MJServer ActionResolver.createActionResult: ResultData = JSON of Output params). */
    private async run(actionID: string, params: Readonly<Record<string, string>>): Promise<OutputParam[]> {
        const variables: OEPGqlVariables = {
            input: { ActionID: actionID, Params: Object.entries(params).map(([Name, Value]) => ({ Name, Value, Type: 'Input' })), SkipActionLog: false },
        };
        let data: RunActionData;
        try {
            data = await this.gql.Execute<RunActionData>(OEP_RUN_ACTION, variables);
        } catch (err) {
            const info = ReadGraphQLError(err);
            if (info === null) throw err;
            throw new OEPActionError(info.Code === 'FORBIDDEN' ? 'FORBIDDEN' : 'FAILED', info.Message.trim() || DEFAULT_FAILURE);
        }
        const r = data.RunAction;
        if (!r.Success) {
            const code = r.ResultCode !== null && IsOEPActionResultCode(r.ResultCode) && r.ResultCode !== 'SUCCESS' ? r.ResultCode : 'FAILED';
            throw new OEPActionError(code, r.Message?.trim() || DEFAULT_FAILURE);
        }
        return r.ResultData ? outputParamList(parseJSON<OutputParam[] | Record<string, OutputParam>>(r.ResultData)) : [];
    }

    /** The named JSON output param. The cast is the typed boundary: the server writes it with JSON.stringify(<the view's server twin>). */
    private output<T>(params: OutputParam[], name: string): T {
        const value = params.find((p) => p.Name === name)?.Value;
        if (!value) throw new OEPActionError('FAILED', DEFAULT_FAILURE);
        return parseJSON<T>(value);
    }
}
